import { api, apiForSession } from './client';
import { GALLERY_PAGE_SIZE, MESSAGE_PAGE_SIZE } from '../lib/deviceProfile';
import type { ChatMuteMode } from '../lib/chatMute';
export { MESSAGE_PAGE_SIZE };
import type { ChatDto, ChatSummary, MemberDto, MessageDto, MessageSend, Page, ReceiptDto } from './types';

export function listChats(): Promise<ChatSummary[]> {
  return api<ChatSummary[]>('/api/v1/chats');
}

/** hasRooms — только для групп (type=2): включить комнаты (темы), см. ChatRoomService.
 *  Можно задать сразу при создании либо включить позже — см. setChatRoomsEnabled ниже. */
export function createChat(type: 1 | 2, title: string | null, userIds: string[], hasRooms?: boolean): Promise<ChatDto> {
  return api<ChatDto>('/api/v1/chats', {
    method: 'POST',
    body: JSON.stringify({ type, title, userIds, hasRooms }),
  });
}

export function getChat(chatId: string): Promise<ChatDto> {
  return api<ChatDto>(`/api/v1/chats/${chatId}`);
}

/** Личный чат "Избранное" (заметки себе) — создаётся при первом обращении, дальше просто
 *  возвращает тот же самый (идемпотентно). */
export function getOrCreateNotesChat(): Promise<ChatDto> {
  return api<ChatDto>('/api/v1/chats/notes', { method: 'POST' });
}

/** Удалить групповой чат целиком (только владелец). */
export function deleteChat(chatId: string) {
  return api(`/api/v1/chats/${chatId}`, { method: 'DELETE' });
}

/** Покинуть чат (или исключить участника, если id — не свой и есть права). */
/** newOwnerId — только когда группу покидает сам владелец: кому передать права (без него
 *  сервер выберет преемника сам — см. ChatService.removeMember). */
export function removeMember(chatId: string, userId: string, newOwnerId?: string) {
  const q = newOwnerId ? `?newOwnerId=${encodeURIComponent(newOwnerId)}` : '';
  return api(`/api/v1/chats/${chatId}/members/${userId}${q}`, { method: 'DELETE' });
}

/** Участники чата с ролью и read-маркером (в отличие от ChatDto.members — там только
 *  плоский список UserBriefDto, без роли, см. backend ChatController.members). */
export function listMembers(chatId: string): Promise<MemberDto[]> {
  return api(`/api/v1/chats/${chatId}/members`);
}

/** Аватар группового чата (только owner/admin). fileId=null — сбросить. */
export function setChatAvatar(chatId: string, fileId: string | null) {
  return api(`/api/v1/chats/${chatId}/avatar`, {
    method: 'PATCH',
    body: JSON.stringify({ fileId }),
  });
}

/** Переименовать групповой чат (только owner/admin). Лимит — 128 символов (см. бэкенд
 *  ChatService.TITLE_MAX_LENGTH); остальным участникам уходит событие title_changed. */
export const CHAT_TITLE_MAX_LENGTH = 128;
export function setChatTitle(chatId: string, title: string) {
  return api(`/api/v1/chats/${chatId}/title`, {
    method: 'PATCH',
    body: JSON.stringify({ title }),
  });
}

/** Включить/выключить комнаты в уже существующей группе (только owner/admin) — см. отчёт
 *  пользователя "настройку для комнат перенеси в настройки самой группы". Выключение ничего
 *  не удаляет, сами комнаты и сообщения в них остаются на сервере. */
export function setChatRoomsEnabled(chatId: string, enabled: boolean) {
  return api(`/api/v1/chats/${chatId}/rooms-enabled`, {
    method: 'PATCH',
    body: JSON.stringify({ enabled }),
  });
}

export function history(chatId: string, before?: number | null, limit = MESSAGE_PAGE_SIZE, roomId?: string | null): Promise<Page<MessageDto>> {
  const q = new URLSearchParams({ limit: String(limit) });
  if (before != null) q.set('before', String(before));
  if (roomId) q.set('roomId', roomId);
  return api<Page<MessageDto>>(`/api/v1/chats/${chatId}/messages?${q}`);
}

// Комнаты (темы) в групповом чате — см. ChatRoomService. Доступны только группам с
// hasRooms=true (см. createChat) — иначе POST/PUT отвечает 400.
export interface ChatRoomDto {
  id: string;
  name: string;
  avatarFileId: string | null;
}

export function listRooms(chatId: string): Promise<ChatRoomDto[]> {
  return api(`/api/v1/chats/${chatId}/rooms`);
}

export function createRoom(chatId: string, name: string, avatarFileId?: string | null): Promise<ChatRoomDto> {
  return api(`/api/v1/chats/${chatId}/rooms`, { method: 'POST', body: JSON.stringify({ name, avatarFileId }) });
}

export function updateRoom(
  chatId: string, roomId: string,
  patch: { name?: string; avatarFileId?: string | null; clearAvatar?: boolean },
): Promise<ChatRoomDto> {
  return api(`/api/v1/chats/${chatId}/rooms/${roomId}`, { method: 'PUT', body: JSON.stringify(patch) });
}

export function deleteRoom(chatId: string, roomId: string) {
  return api(`/api/v1/chats/${chatId}/rooms/${roomId}`, { method: 'DELETE' });
}

/** Назначить админа/участника группы (owner/admin группы). */
export function setMemberRole(chatId: string, userId: string, role: 'admin' | 'member' | 'owner') {
  return api(`/api/v1/chats/${chatId}/members/${userId}/role`, { method: 'POST', body: JSON.stringify({ role }) });
}

export function searchUsers(q: string): Promise<{ id: string; username: string; displayName: string }[]> {
  return api(`/api/v1/users/search?q=${encodeURIComponent(q)}&limit=10`);
}

/**
 * Id вложений сообщения (payload.data.media[].fileId) — для поля mediaIds запроса, которое
 * сервер использует для контроля доступа к файлам (см. MessageSend.mediaIds). Картинку превью
 * ссылки (linkPreview.imageFileId) сюда сознательно НЕ кладём: это общий кэш по URL, а не
 * вложение отправителя.
 */
export function mediaIdsOf(payload: unknown): string[] {
  const media = (payload as { data?: { media?: unknown } } | null)?.data?.media;
  if (!Array.isArray(media)) return [];
  const ids = media
    .map((m) => (m && typeof m === 'object' ? (m as { fileId?: unknown }).fileId : undefined))
    .filter((id): id is string => typeof id === 'string' && id.length > 0);
  return Array.from(new Set(ids));
}

/** REST-fallback отправки (когда WS недоступен). Единственная точка отправки веб-клиента —
 *  здесь же проставляем mediaIds из payload (включая пересылку). */
export function sendMessage(chatId: string, body: MessageSend, session?: string, signal?: AbortSignal): Promise<MessageDto> {
  const withMedia: MessageSend = { ...body, mediaIds: body.mediaIds ?? mediaIdsOf(body.payload) };
  const request = {
    method: 'POST',
    body: JSON.stringify(withMedia),
    signal,
  };
  const path = `/api/v1/chats/${chatId}/messages`;
  return session === undefined ? api<MessageDto>(path, request) : apiForSession<MessageDto>(session, path, request);
}

/**
 * Правка сообщения: обёртка {payload, mediaIds} — набор вложений на сервере заменяется
 * целиком по mediaIds (вложение, выпавшее из payload, перестаёт быть доступным участникам).
 */
export function editMessagePayload(chatId: string, messageId: number, payload: Record<string, unknown>): Promise<MessageDto> {
  return api<MessageDto>(`/api/v1/chats/${chatId}/messages/${messageId}`, {
    method: 'PATCH',
    body: JSON.stringify({ payload, mediaIds: mediaIdsOf(payload) }),
  });
}

export function searchMessages(chatId: string, q: string): Promise<MessageDto[]> {
  return api(`/api/v1/chats/${chatId}/messages/search?q=${encodeURIComponent(q)}&limit=30`);
}

export type GalleryKind = 'media' | 'music' | 'voice' | 'file' | 'link';

/** Галерея вложений чата для инфо-панели (Медиа/Музыка/Голосовые/Документы/Ссылки), курсорная пагинация. */
export function getChatGallery(
  chatId: string,
  kind: GalleryKind,
  before?: number | null,
  limit = GALLERY_PAGE_SIZE,
): Promise<Page<MessageDto>> {
  const q = new URLSearchParams({ kind, limit: String(limit) });
  if (before) q.set('before', String(before));
  return api<Page<MessageDto>>(`/api/v1/chats/${chatId}/messages/gallery?${q}`);
}

/** Групповые чаты, общие с собеседником этого личного чата (вкладка "Группы" инфопанели). */
export function getCommonGroups(chatId: string): Promise<ChatSummary[]> {
  return api<ChatSummary[]>(`/api/v1/chats/${chatId}/common-groups`);
}

export function markRead(chatId: string, messageId: number, roomId: string | null = null) {
  return api(`/api/v1/chats/${chatId}/read`, {
    method: 'POST',
    body: JSON.stringify({ messageId, roomId }),
  });
}

export function markDelivered(chatId: string, messageId: number, roomId: string | null = null) {
  return api(`/api/v1/chats/${chatId}/delivered`, {
    method: 'POST',
    body: JSON.stringify({ messageId, roomId }),
  });
}

export function pinChat(chatId: string) {
  return api(`/api/v1/chats/${chatId}/pin`, { method: 'POST' });
}

export function unpinChat(chatId: string) {
  return api(`/api/v1/chats/${chatId}/pin`, { method: 'DELETE' });
}

export function archiveChat(chatId: string) {
  return api(`/api/v1/chats/${chatId}/archive`, { method: 'POST' });
}

export function unarchiveChat(chatId: string) {
  return api(`/api/v1/chats/${chatId}/archive`, { method: 'DELETE' });
}

export type ChatMuteRequest = { mode: ChatMuteMode; muteUntil?: string };

export function setChatMute(chatId: string, mode: ChatMuteMode, muteUntil?: string): Promise<void> {
  const body: ChatMuteRequest = { mode };
  if (mode === 'custom' && muteUntil != null) body.muteUntil = muteUntil;
  return api<void>(`/api/v1/chats/${chatId}/mute`, { method: 'PUT', body: JSON.stringify(body) });
}

/**
 * emoji=null снимает свою реакцию; тот же emoji, что уже стоит, — тоже снимает (тоггл);
 * другой — заменяет. Ответ приходит и через message_edited (WS/polling) всем участникам,
 * этот же вызов возвращает актуальную MessageDto сразу для того, кто нажал.
 */
export function setMessageReaction(chatId: string, messageId: number, emoji: string | null): Promise<MessageDto> {
  return api<MessageDto>(`/api/v1/chats/${chatId}/messages/${messageId}/reactions`, {
    method: 'PUT',
    body: JSON.stringify({ emoji }),
  });
}

export type PinScope = 'all' | 'me';

/**
 * scope='all' — общее закрепление (видно всем участникам, приходит всем через message_edited).
 * scope='me' — личное ("для себя"): ответ содержит personalPinnedAt только для вызвавшего,
 * остальным участникам ничего не транслируется — патчить локальный стейт нужно этим же ответом.
 */
export function setMessagePin(chatId: string, messageId: number, scope: PinScope, pinned: boolean): Promise<MessageDto> {
  return api<MessageDto>(`/api/v1/chats/${chatId}/messages/${messageId}/pin`, {
    method: 'PUT',
    body: JSON.stringify({ scope, pinned }),
  });
}

export function getPinnedMessages(chatId: string, scope: PinScope): Promise<MessageDto[]> {
  return api(`/api/v1/chats/${chatId}/messages/pinned?scope=${scope}`);
}

/** Добавить в группу сразу нескольких (уже состоящие молча пропускаются). */
export function addMembers(chatId: string, userIds: string[]) {
  return api(`/api/v1/chats/${chatId}/members`, { method: 'POST', body: JSON.stringify({ userIds }) });
}

/** Токен ссылки-приглашения группы (owner/admin; нет — создаётся). Ссылка: `${origin}/join/${token}`. */
export function getChatInvite(chatId: string): Promise<{ token: string }> {
  return api(`/api/v1/chats/${chatId}/invite`);
}

/** Отозвать текущую ссылку и получить новую. */
export function resetChatInvite(chatId: string): Promise<{ token: string }> {
  return api(`/api/v1/chats/${chatId}/invite/reset`, { method: 'POST' });
}

export interface InvitePreview {
  chatId: string;
  title: string;
  memberCount: number;
  alreadyMember: boolean;
}

export function previewInvite(token: string): Promise<InvitePreview> {
  return api(`/api/v1/invites/${encodeURIComponent(token)}`);
}

export function joinByInvite(token: string): Promise<{ chatId: string }> {
  return api(`/api/v1/invites/${encodeURIComponent(token)}/join`, { method: 'POST' });
}

/** «Информация» о своём сообщении: по каждому получателю — доставлено/прочитано и когда. */
export function getMessageReceipts(chatId: string, messageId: number): Promise<ReceiptDto[]> {
  return api<ReceiptDto[]>(`/api/v1/chats/${chatId}/messages/${messageId}/receipts`);
}
